// crewbit-admin/src/components/money/settings/SaveConfirmationPill.tsx
//
// Inline-save flash pill — fades in for ~2s after a debounced save lands,
// then fades out. Driven by a `visible` prop the parent toggles via a
// useState + setTimeout pattern. No internal timer (caller owns lifecycle).
//
// Per Decision §Settings.D3 — every editor field auto-saves after 1.5s
// debounce; this pill confirms the save without blocking the operator with
// an explicit Save button. Mirrors mobile SaveConfirmationPill but uses
// CSS opacity transition instead of Reanimated.

import { Check, AlertTriangle } from 'lucide-react';
import { palette, radii, spacing, transition } from '../../../styles/tokens';

export type SaveConfirmationKind = 'success' | 'warning';

interface SaveConfirmationPillProps {
  visible: boolean;
  kind?: SaveConfirmationKind;
  /** When omitted, defaults: success → "Saved", warning → "Couldn't save". */
  label?: string;
}

interface Tone {
  bg: string;
  fg: string;
  Icon: typeof Check;
}

const TONE: Record<SaveConfirmationKind, Tone> = {
  success: { bg: palette.greenWash, fg: palette.green, Icon: Check },
  warning: { bg: palette.amberWash, fg: palette.amber, Icon: AlertTriangle },
};

export default function SaveConfirmationPill({
  visible,
  kind = 'success',
  label,
}: SaveConfirmationPillProps) {
  const tone = TONE[kind];
  const text = label ?? (kind === 'success' ? 'Saved' : "Couldn't save");
  return (
    <span
      aria-hidden={!visible}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing.xs,
        backgroundColor: tone.bg,
        color: tone.fg,
        borderRadius: radii.sm,
        paddingInline: spacing.sm,
        paddingBlock: spacing.xs,
        fontSize: 11.5,
        fontWeight: 600,
        opacity: visible ? 1 : 0,
        transition: `opacity ${transition.normal}`,
        pointerEvents: 'none',
        lineHeight: 1.2,
      }}
    >
      <tone.Icon size={12} color={tone.fg} strokeWidth={2.5} />
      {text}
    </span>
  );
}
